/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable chai-friendly/no-unused-expressions */
import PropTypes from 'prop-types';
import { HotelCard, CruiseCard, Grid, GridItem } from '@bwoty-web/ui-kit';
import discountTypeEnum from '../../shared/constants/discountType';
import ValidationContainer from './validation/validationContainer';
import ValidationWrapper from './validation/validationWrapper';
import priceOfferTypes from '../constants/priceOfferTypes';

import './priceOfferContainer.scss';

const getLogTypeTitle = (type) => {
  switch (type) {
    case priceOfferTypes.CRUISE:
      return {
        event: 'cruisePriceOffer',
        action: 'CruisePackage Price Offer',
      };
    case priceOfferTypes.CHARTER:
      return {
        event: 'charterPriceOffer',
        action: 'CharterPackage Price Offer',
      };
    case priceOfferTypes.INDEPENDENT:
      return {
        event: 'independentPriceOffer',
        action: 'IndependentPackage Price Offer',
      };
    case priceOfferTypes.HOTEL_ONLY:
      return {
        event: 'expediaPriceOffer',
        action: 'CharterPackage Price Offer',
      };
    case priceOfferTypes.GEOGRAPHICAL:
      return {
        event: 'geographicPriceOffer',
        action: 'Geographic Price Offer',
      };
    default:
      return {
        event: 'unkownPriceOffer',
        action: 'UnkownPackage Price Offer',
      };
  }
};

const discountTypeTranslation = (type) => {
  switch (type) {
    case 'showDiscount':
      return discountTypeEnum.showDiscount;
    case 'dontShow':
      return discountTypeEnum.dontShow;
    case 'showOrdinaryPrice':
      return discountTypeEnum.showOrdinaryPrice;
    default:
      return type;
  }
};

const createEventLabelFromTitle = (title) => {
  return title.replace(/ /g, '_').replace(/'/g, '_').toLowerCase();
};

export const getPackageInformationForLog = (packages) => {
  const [firstPack] = packages;
  const logTitles = getLogTypeTitle(firstPack.type);
  const contentCodes =
    firstPack.type === priceOfferTypes.HOTEL_ONLY
      ? packages.map((pack) => createEventLabelFromTitle(pack.title))
      : packages.map((pack) => pack.contentCode);

  return {
    type: firstPack.type,
    contentCodes: contentCodes.join(', '),
    ...logTitles,
  };
};

const PriceOfferContainer = ({
  id,
  priceOfferId,
  packages,
  discountType,
  heading,
  buttonText,
  validations,
  sectionTitle,
}) => {

  const hasPackages = packages && packages.length > 0;
  if (!hasPackages && !validations) {
    return null;
  }

  if (!hasPackages && validations) {
    return (
      <ValidationContainer
        priceOfferId={priceOfferId}
        validations={validations}
      />
    );
  }

  const log = getPackageInformationForLog(packages);

  return (
    <div
      className="price-offer-container"
      data-id={priceOfferId}
      data-event={log.event}
      data-action={log.action}
      data-category="Price Offer"
      data-label={log.contentCodes}
      id={id}
    >
      {validations && (
        <ValidationContainer
          priceOfferId={priceOfferId}
          validations={validations}
        />
      )}
      {(heading || sectionTitle) && (
        <div style={{ marginBottom: 24 }}>
          {heading && (
            <div className="uikit-font-section-heading">{heading}</div>
          )}

          {sectionTitle && (
            <div
              style={{ marginTop: 16 }}
              className="uikit-font-preamble"
            >
              {sectionTitle}
            </div>
          )}
        </div>
      )}

      <Grid>
        {packages.map((priceOfferPackage) => {
          const tags = priceOfferPackage.targetGroupText
            ? [priceOfferPackage.targetGroupText]
            : [];

          const concept =
            !!priceOfferPackage.concept && !!priceOfferPackage.concept.name
              ? priceOfferPackage.concept.name
              : null;

          const isOriginalPriceEqualToPay =
            priceOfferPackage?.price?.original ===
            priceOfferPackage?.price?.toPay;

          let discountDisplayType = discountTypeEnum.showDiscount;
          if (!priceOfferPackage?.price?.showDiscountedPrice)
            discountDisplayType = discountTypeEnum.dontShow;
          else if (
            priceOfferPackage?.price?.showDiscountedPrice &&
            (isOriginalPriceEqualToPay || !priceOfferPackage.price.original)
          )
            discountDisplayType = discountTypeEnum.showForcedDiscount;
          else discountDisplayType = discountTypeTranslation(discountType);

          const cardProps = {
            className: 'price-offer-card',
            imagePosition: 'top',
            concept,
            columnSpan: 4,
            title: priceOfferPackage.title,
            subtitle: priceOfferPackage.subtitle,
            image: {
              cloudinaryUriTemplate: priceOfferPackage.imageUrl,
            },
            buttonText: buttonText || priceOfferPackage.buttonText,
            url: priceOfferPackage.url,
            pricePerPerson: priceOfferPackage.price.toPay,
            ...(priceOfferPackage?.price?.showDiscountedPrice && {
              originalPricePerPerson: priceOfferPackage.price.original,
            }),
            bullets: priceOfferPackage.uspList,
            rating: priceOfferPackage.rating,
            ksps: [
              priceOfferPackage.mealDescription,
              priceOfferPackage.luggageIncludedDescription,
              ...(priceOfferPackage.kspList || []),
            ].filter((k) => k),
            tags,
            discountDisplayType,
          };

          const notPricePerPerson =
            priceOfferPackage.price?.labels.priceDescription?.[0] === '2';

          const potentialCardOverrides = notPricePerPerson
            ? {
                footerDescription:
                  priceOfferPackage.price?.labels?.priceDescription,
                price: priceOfferPackage.price?.toPay,
                originalPrice: priceOfferPackage.price?.original,
                pricePerPerson: undefined,
                originalPricePerPerson: undefined,
                pricePerPersonFocus: false,
              }
            : {};

          return (
            <GridItem
              columnSpan={4}
              key={priceOfferPackage.title}
            >
              <ValidationWrapper
                validations={validations}
                id={priceOfferPackage.validationId}
              >
                {priceOfferPackage.type === 'CRUISE' ? (
                  <CruiseCard
                    {...cardProps}
                    ratingPrefix={priceOfferPackage.shipName}
                    pricePerPersonFocus
                    {...potentialCardOverrides}
                  />
                ) : (
                  <HotelCard
                    {...cardProps}
                    pricePerPersonFocus
                    {...potentialCardOverrides}
                  />
                )}
              </ValidationWrapper>
            </GridItem>
          );
        })}
      </Grid>
    </div>
  );
};

PriceOfferContainer.propTypes = {
  id: PropTypes.string,
  priceOfferId: PropTypes.number.isRequired,
  packages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  mucd: PropTypes.number.isRequired,
  discountType: PropTypes.string,
  // eslint-disable-next-line react/require-default-props
  buttonText: PropTypes.string, // TODO: Vi borde kanske lösa detta på ett bättre sätt?
  heading: PropTypes.string,
  validations: PropTypes.arrayOf(PropTypes.shape({})),
  sectionTitle: PropTypes.string,
};

PriceOfferContainer.defaultProps = {
  discountType: discountTypeEnum.showDiscount,
  heading: undefined,
  validations: null,
  sectionTitle: undefined,
  id: undefined,
};

export default PriceOfferContainer;
